import Phaser from 'phaser';
import { GAME_WIDTH, GAME_HEIGHT } from '../config/GameConfig';
import type Player from '../entities/Player';
import type Difficulty from '../systems/Difficulty';

export interface HUDStats {
  score: number;
  highScore: number;
  kills: number;
}

/**
 * 技能槽 HUD 元素：3 个右下角圆按钮（A1 / A2 / U）共享的内部结构
 * 由 _buildSkillButton 创建并塞进 _skillButtons 数组
 */
interface SkillButton {
  slot: 'a1' | 'a2' | 'ult';
  bg: Phaser.GameObjects.Arc;
  ring: Phaser.GameObjects.Arc;
  icon: Phaser.GameObjects.Text;
  /** 状态文字（CD 倒计时 / ULT 充能数字） */
  statusText: Phaser.GameObjects.Text;
  /** 用 graphics 画扇形进度作为冷却 / 充能遮罩 */
  mask: Phaser.GameObjects.Graphics;
  x: number;
  y: number;
  r: number;
  tint: number;
}

/**
 * HUD —— 屏内 HUD 组件聚合
 *
 * 顶部信息层（v2 折叠版）：
 *   - 中央：得分（22px）+ 最高分（10px 副行）
 *   - 中央下方：心条（≥10 时切紧凑文本）
 *   - 左上：4 项核心徽章（波次 / 击杀 / 金币 / 护盾）—— 图标 + 数字，比纯文字省空间
 *   - 右上：暂停按钮 + 📊 折叠详情按钮（点击切换详细 stats 面板）
 *   - Boss 在场时：屏顶全屏 Boss 血条 + 名字 + 阶段
 *
 * 由 MainScene 直接持有；面板（升级 / 暂停 / GameOver）由 PanelManager 管，互不耦合。
 */
export default class HUD {
  scene: Phaser.Scene;
  player: Player;
  difficulty: Difficulty;

  scoreText: Phaser.GameObjects.Text;
  highScoreText: Phaser.GameObjects.Text;
  hearts: Phaser.GameObjects.Text[] = [];
  /**
   * 紧凑生命显示：当 maxHp ≥ HEART_COMPACT_THRESHOLD 时改成"♥ 当前/上限"，
   * 避免后期一排红心顶满屏。这时 hearts 里只有一个 Text。
   */
  private _heartCompact = false;

  /** 顶部左侧 4 项核心徽章（图标 + 数字） */
  private _badges!: {
    wave: Phaser.GameObjects.Text;
    kill: Phaser.GameObjects.Text;
    coin: Phaser.GameObjects.Text;
    shield: Phaser.GameObjects.Text;
  };

  /** 详细 stats 折叠面板 —— 默认隐藏，点 📊 按钮切换 */
  private _detailsPanel: Phaser.GameObjects.Container | null = null;
  private _detailsTexts: {
    fire: Phaser.GameObjects.Text;
    dmg: Phaser.GameObjects.Text;
    shot: Phaser.GameObjects.Text;
    chest: Phaser.GameObjects.Text;
  } | null = null;
  private _detailsVisible = false;

  /** Boss 全屏血条（仅 Boss 在场时显示） */
  private _bossBar: {
    container: Phaser.GameObjects.Container;
    bgRect: Phaser.GameObjects.Rectangle;
    fillRect: Phaser.GameObjects.Rectangle;
    nameText: Phaser.GameObjects.Text;
    phaseText: Phaser.GameObjects.Text;
    fullW: number;
  } | null = null;

  /** 暂停按钮交互区域；外部用 onPauseClick 注册回调 */
  private _pauseBtnBg: Phaser.GameObjects.Arc;
  /** 详情切换按钮（📊） */
  private _detailsBtnBg!: Phaser.GameObjects.Arc;
  onPauseClick: (() => void) | null = null;

  /** 闪现按钮（右下角）—— 圆环 + 进度遮罩 + 中央 ⚡ */
  // 旧版：单按钮硬编码 ⚡；现版改成 3 个技能槽（A1 / A2 / U），由 _skillButtons 统一管理
  private _skillButtons: SkillButton[] = [];
  /** 兼容旧外部接口：onDashClick 仍保留，等同于点击 A1（默认是 dash） */
  onDashClick: (() => void) | null = null;
  /** A2 / ULT 点击回调（MainScene 注入） */
  onA2Click: (() => void) | null = null;
  onUltClick: (() => void) | null = null;

  /** 缓存最近一次的击杀 + 宝箱进度，详情切换显示时不需要外部重传 */
  private _lastKills = 0;
  private _lastChestProgress: { collected: number; threshold: number } | null = null;

  constructor(scene: Phaser.Scene, player: Player, difficulty: Difficulty, initialHighScore: number) {
    this.scene = scene;
    this.player = player;
    this.difficulty = difficulty;

    // 顶部分数 + 最高分（向上抬一点，给 Boss 血条留位置）
    this.scene.add
      .rectangle(GAME_WIDTH / 2, 0, 190, 60, 0x000000, 0.32)
      .setOrigin(0.5, 0)
      .setDepth(98)
      .setStrokeStyle(1, 0x4cd2ff, 0.18);
    this.scoreText = scene.add
      .text(GAME_WIDTH / 2, 12, '得分 0', {
        fontFamily: 'sans-serif',
        fontSize: '24px',
        fontStyle: 'bold',
        color: '#e8f1ff',
        stroke: '#06101f',
        strokeThickness: 3,
      })
      .setOrigin(0.5, 0)
      .setDepth(100);
    this.highScoreText = scene.add
      .text(GAME_WIDTH / 2, 40, `最高 ${initialHighScore}`, {
        fontFamily: 'sans-serif',
        fontSize: '11px',
        color: '#ffd166',
        stroke: '#06101f',
        strokeThickness: 2,
      })
      .setOrigin(0.5, 0)
      .setDepth(100);

    // 顶部右侧两个圆按钮：详情（左）+ 暂停（右）
    this._pauseBtnBg = this._buildPauseButton();
    this._detailsBtnBg = this._buildDetailsButton();

    // 闪现按钮（右下角）
    this._buildSkillButtons();

    // 顶部左侧核心徽章
    this._buildBadgeRow();

    // 详细 stats 折叠面板（默认隐藏）
    this._buildDetailsPanel();

    // ❤️ 血条
    this._buildHeartBar(player.maxHp);

    // 开局分割线提示 —— 右半屏轻微暗化（永久）+ 中央竖线开局 2.5s 闪一下淡出
    this._buildSplitHint();
  }

  /**
   * 右下角技能区视觉提示（v4）：
   *   - 右下角 80×260 矩形铺一层 alpha=0.22 暗色（明显地"这是按钮区"）
   *   - 左缘 + 上缘 各画 1px 青色线，让玩家眼角余光感知边界
   */
  private _buildSplitHint(): void {
    const zoneW = 80;
    const zoneH = 260;
    const zoneX = GAME_WIDTH - zoneW;
    const zoneY = GAME_HEIGHT - zoneH;
    // 永久暗色背景
    this.scene.add
      .rectangle(zoneX, zoneY, zoneW, zoneH, 0x000000, 0.22)
      .setOrigin(0, 0)
      .setDepth(5);
    // 左缘 1px 青色分隔线
    this.scene.add
      .rectangle(zoneX, zoneY, 1, zoneH, 0x4cd2ff, 0.35)
      .setOrigin(0, 0)
      .setDepth(6);
    // 上缘 1px 青色分隔线
    this.scene.add
      .rectangle(zoneX, zoneY, zoneW, 1, 0x4cd2ff, 0.35)
      .setOrigin(0, 0)
      .setDepth(6);
  }

  // ─── 构建辅助 ────────────────────────────────────────────────────

  private _buildPauseButton(): Phaser.GameObjects.Arc {
    const r = 16;
    const x = GAME_WIDTH - r - 10;
    const y = r + 10;

    const bg = this.scene.add.circle(x, y, r, 0x000000, 0.4).setDepth(101);
    bg.setStrokeStyle(2, 0xffffff, 0.6);

    this.scene.add
      .text(x, y - 1, '‖', {
        fontFamily: 'sans-serif',
        fontSize: '18px',
        fontStyle: 'bold',
        color: '#ffffff',
      })
      .setOrigin(0.5)
      .setDepth(102);

    bg.setInteractive({ useHandCursor: true });
    bg.on('pointerdown', (
      _p: Phaser.Input.Pointer,
      _lx: number,
      _ly: number,
      e?: Phaser.Types.Input.EventData,
    ) => {
      e?.stopPropagation();
      this.onPauseClick?.();
    });
    bg.on('pointerover', () => bg.setStrokeStyle(2, 0x4cd2ff, 1));
    bg.on('pointerout', () => bg.setStrokeStyle(2, 0xffffff, 0.6));
    return bg;
  }

  /** 详情切换按钮（📊）—— 右上角第二个圆 */
  private _buildDetailsButton(): Phaser.GameObjects.Arc {
    const r = 16;
    const x = GAME_WIDTH - r * 2 - r - 10 - 8; // 距暂停按钮左侧 8px
    const y = r + 10;

    const bg = this.scene.add.circle(x, y, r, 0x000000, 0.4).setDepth(101);
    bg.setStrokeStyle(2, 0xffffff, 0.6);

    const icon = this.scene.add
      .text(x, y, '📊', {
        fontFamily: 'sans-serif',
        fontSize: '15px',
      })
      .setOrigin(0.5)
      .setDepth(102);

    bg.setInteractive({ useHandCursor: true });
    bg.on('pointerdown', (
      _p: Phaser.Input.Pointer,
      _lx: number,
      _ly: number,
      e?: Phaser.Types.Input.EventData,
    ) => {
      e?.stopPropagation();
      this._toggleDetails();
    });
    bg.on('pointerover', () => bg.setStrokeStyle(2, 0x4cd2ff, 1));
    bg.on('pointerout', () => bg.setStrokeStyle(2, 0xffffff, 0.6));
    // 阻止图标自身吞掉事件
    icon.setInteractive({ useHandCursor: true });
    icon.on('pointerdown', (
      _p: Phaser.Input.Pointer,
      _lx: number,
      _ly: number,
      e?: Phaser.Types.Input.EventData,
    ) => {
      e?.stopPropagation();
      this._toggleDetails();
    });
    return bg;
  }

  /**
   * 顶部左上 4 项核心徽章：每个 38×20 的小条，icon + 数字
   * 布局横排：W K $ S（波次 / 击杀 / 金币 / 护盾）
   */
  private _buildBadgeRow(): void {
    const x0 = 8;
    const y = 12;
    const cellW = 60;
    const style: Phaser.Types.GameObjects.Text.TextStyle = {
      fontFamily: 'sans-serif',
      fontSize: '13px',
      fontStyle: 'bold',
      color: '#e8f1ff',
    };
    // 半透明底
    this.scene.add
      .rectangle(x0 - 6, y - 6, cellW * 2 + 12, 48, 0x000000, 0.36)
      .setOrigin(0, 0)
      .setDepth(98)
      .setStrokeStyle(1, 0x4cd2ff, 0.16);
    this._badges = {
      wave:   this.scene.add.text(x0,           y, '波 1',   { ...style, color: '#ffd166' }).setDepth(100),
      kill:   this.scene.add.text(x0 + cellW,   y, '杀 0',   { ...style, color: '#cfd6e0' }).setDepth(100),
      coin:   this.scene.add.text(x0,           y + 22, '币 0',  { ...style, color: '#ffe066' }).setDepth(100),
      shield: this.scene.add.text(x0 + cellW,   y + 22, '盾 0',  { ...style, color: '#4cd2ff' }).setDepth(100),
    };
  }

  /**
   * 详细 stats 折叠面板：默认隐藏。点 📊 切换。
   * 4 行：射速 / 伤害（含穿/追/反/暴/吸 修饰）/ 弹数 / 宝箱进度
   */
  private _buildDetailsPanel(): void {
    const w = 180;
    const h = 84;
    const x = GAME_WIDTH - w - 10;
    const y = 56;
    const c = this.scene.add.container(x, y).setDepth(120);
    const bg = this.scene.add
      .rectangle(0, 0, w, h, 0x0a1830, 0.92)
      .setOrigin(0, 0)
      .setStrokeStyle(1, 0x4cd2ff, 0.45);
    c.add(bg);
    const lineH = 18;
    const style: Phaser.Types.GameObjects.Text.TextStyle = {
      fontFamily: 'sans-serif',
      fontSize: '12px',
      color: '#cfd6e0',
    };
    const fire  = this.scene.add.text(8, 6 + lineH * 0, '射速  280ms', style);
    const dmg   = this.scene.add.text(8, 6 + lineH * 1, '伤害  5', style);
    const shot  = this.scene.add.text(8, 6 + lineH * 2, '弹数  1', style);
    const chest = this.scene.add.text(8, 6 + lineH * 3, '宝箱  0/3', { ...style, color: '#ffb84a' });
    c.add([fire, dmg, shot, chest]);
    c.setVisible(false);
    this._detailsPanel = c;
    this._detailsTexts = { fire, dmg, shot, chest };
  }

  private _toggleDetails(): void {
    this._detailsVisible = !this._detailsVisible;
    if (this._detailsPanel) this._detailsPanel.setVisible(this._detailsVisible);
  }

  /**
   * 右下角技能堆叠布局（v4）：
   *   - 三个按钮垂直堆叠在右下角，区域 80 × 260（左缘 x=370、上缘 y=540、距右下 64px）
   *   - 顺序（从下到上）：A1 → A2 → ULT —— 最常按的 A1 在拇指最近的右下，
   *     大招 ULT 放最上"压轴"位（蓄满时玩家眼睛先看到顶部）
   *   - 按钮 r=28（A1/A2），r=32（ULT，最大，强调大招地位）
   */
  private _buildSkillButtons(): void {
    const cx = GAME_WIDTH - 40;                  // 区域水平中线（从右边距 40）
    const baseY = GAME_HEIGHT - 64 - 28;         // 最下按钮中心 y（距底 64 + r）
    const gap = 80;                               // 按钮中心到中心间距

    // A1（最下，主操作）—— 右拇指自然落点
    const a1 = this._buildSkillButton('a1', cx, baseY, 28, 0x4cd2ff);
    // A2（中，副）
    const a2 = this._buildSkillButton('a2', cx, baseY - gap, 28, 0xffd166);
    // ULT（最上，最大）—— 大招"压轴位"，蓄满时最显眼
    const ult = this._buildSkillButton('ult', cx, baseY - gap * 2, 32, 0xff5566);

    a1.bg.on('pointerdown', (
      _p: Phaser.Input.Pointer, _lx: number, _ly: number, e?: Phaser.Types.Input.EventData,
    ) => { e?.stopPropagation(); this.onDashClick?.(); });
    a2.bg.on('pointerdown', (
      _p: Phaser.Input.Pointer, _lx: number, _ly: number, e?: Phaser.Types.Input.EventData,
    ) => { e?.stopPropagation(); this.onA2Click?.(); });
    ult.bg.on('pointerdown', (
      _p: Phaser.Input.Pointer, _lx: number, _ly: number, e?: Phaser.Types.Input.EventData,
    ) => { e?.stopPropagation(); this.onUltClick?.(); });

    this._skillButtons = [a1, a2, ult];
  }

  private _buildSkillButton(slot: SkillButton['slot'], x: number, y: number, r: number, tint: number): SkillButton {
    const bg = this.scene.add
      .circle(x, y, r, 0x0a1830, 0.82)
      .setDepth(101)
      .setStrokeStyle(3, tint, 0.95);
    const mask = this.scene.add.graphics().setDepth(102);
    const glow = this.scene.add
      .circle(x, y, r + 10, tint, 0.12)
      .setDepth(99);
    this.scene.tweens.add({
      targets: glow,
      alpha: { from: 0.08, to: 0.18 },
      scale: { from: 0.96, to: 1.04 },
      duration: 1200,
      yoyo: true,
      repeat: -1,
      ease: 'Sine.easeInOut',
    });
    const ring = this.scene.add
      .circle(x, y, r + 4, 0x000000, 0)
      .setDepth(100)
      .setStrokeStyle(2, tint, 0.4);
    const icon = this.scene.add
      .text(x, y - 2, '?', {
        fontFamily: 'sans-serif',
        fontSize: `${Math.round(r * 0.95)}px`,
        fontStyle: 'bold',
        color: '#' + tint.toString(16).padStart(6, '0'),
      })
      .setOrigin(0.5)
      .setDepth(103);
    const statusText = this.scene.add
      .text(x, y + r + 2, '', {
        fontFamily: 'sans-serif',
        fontSize: '11px',
        fontStyle: 'bold',
        color: '#aac4d8',
        stroke: '#000000',
        strokeThickness: 2,
      })
      .setOrigin(0.5, 0)
      .setDepth(104);
    bg.setInteractive({ useHandCursor: true });
    bg.on('pointerover', () => bg.setStrokeStyle(3, 0xffffff, 1));
    bg.on('pointerout', () => bg.setStrokeStyle(3, tint, 0.95));
    return { slot, bg, ring, icon, statusText, mask, x, y, r, tint };
  }

  /**
   * 每帧由 MainScene 调用一次：刷新 3 个技能槽的图标 / CD 遮罩 / 充能进度
   * 入参为 SkillSystem.snapshot() 的结果
   */
  refreshSkillSlots(snap: {
    a1: { cfg: { icon: string; tint: number }; ratio: number; leftMs: number };
    a2: { cfg: { icon: string; tint: number }; ratio: number; leftMs: number };
    ult: {
      cfg: { icon: string; tint: number; chargeMax: number };
      ratio: number;
      charge: number;
      isActive: boolean;
    };
  }): void {
    if (this._skillButtons.length < 3) return;
    const [a1, a2, ult] = this._skillButtons;
    this._refreshActiveButton(a1, snap.a1);
    this._refreshActiveButton(a2, snap.a2);
    this._refreshUltButton(ult, snap.ult);
  }

  /** 主动技能按钮：CD 遮罩 + 图标颜色（就绪 = tint，CD 中 = 灰）+ 状态文字（CD 秒数） */
  private _refreshActiveButton(
    btn: SkillButton,
    snap: { cfg: { icon: string; tint: number }; ratio: number; leftMs: number },
  ): void {
    const ready = snap.ratio >= 1;
    const tintHex = '#' + snap.cfg.tint.toString(16).padStart(6, '0');
    btn.icon.setText(snap.cfg.icon);

    btn.mask.clear();
    if (!ready) {
      // 从 -π/2（顶部）顺时针扫 (1-ratio) × 2π 的扇形 = 还没就绪的部分
      const startA = -Math.PI / 2;
      const endA = startA + (1 - snap.ratio) * Math.PI * 2;
      btn.mask.fillStyle(0x000000, 0.62);
      btn.mask.beginPath();
      btn.mask.moveTo(btn.x, btn.y);
      btn.mask.arc(btn.x, btn.y, btn.r - 2, endA, startA + Math.PI * 2, false);
      btn.mask.closePath();
      btn.mask.fillPath();
    }

    if (ready) {
      btn.icon.setColor(tintHex);
      btn.bg.setStrokeStyle(3, snap.cfg.tint, 0.95);
      btn.ring.setStrokeStyle(2, snap.cfg.tint, 0.85);
      btn.statusText.setText('');
    } else {
      btn.icon.setColor('#aac4d8');
      btn.bg.setStrokeStyle(3, 0x6a7a90, 0.85);
      btn.ring.setStrokeStyle(2, 0x6a7a90, 0.4);
      const sec = (snap.leftMs / 1000).toFixed(snap.leftMs > 1000 ? 0 : 1);
      btn.statusText.setText(`${sec}s`);
    }
  }

  /**
   * ULT 按钮：圆环作为充能进度（顺时针填充），就绪后图标转金色 + 圆环搏动；
   * 释放期 isActive=true 时图标更亮 + 边框白色
   */
  private _refreshUltButton(
    btn: SkillButton,
    snap: { cfg: { icon: string; tint: number; chargeMax: number }; ratio: number; charge: number; isActive: boolean },
  ): void {
    const ready = snap.ratio >= 1;
    const tintHex = '#' + snap.cfg.tint.toString(16).padStart(6, '0');
    btn.icon.setText(snap.cfg.icon);

    // 充能遮罩：未充满时把"未充能"那段画灰；与主动按钮 CD 反过来 —— 这里 ratio 是充能进度（充满 1）
    btn.mask.clear();
    if (!ready) {
      const startA = -Math.PI / 2;
      const endA = startA + (1 - snap.ratio) * Math.PI * 2;
      btn.mask.fillStyle(0x000000, 0.62);
      btn.mask.beginPath();
      btn.mask.moveTo(btn.x, btn.y);
      btn.mask.arc(btn.x, btn.y, btn.r - 2, endA, startA + Math.PI * 2, false);
      btn.mask.closePath();
      btn.mask.fillPath();
    }

    if (snap.isActive) {
      // 释放期：白色边框 + 高亮
      btn.icon.setColor('#ffffff');
      btn.bg.setStrokeStyle(3, 0xffffff, 1);
      btn.ring.setStrokeStyle(2, 0xffffff, 0.95);
      btn.statusText.setText('使用中');
    } else if (ready) {
      btn.icon.setColor(tintHex);
      btn.bg.setStrokeStyle(3, snap.cfg.tint, 1);
      btn.ring.setStrokeStyle(2, snap.cfg.tint, 0.95);
      btn.statusText.setText('就绪!');
    } else {
      btn.icon.setColor('#aac4d8');
      btn.bg.setStrokeStyle(3, 0x6a7a90, 0.85);
      btn.ring.setStrokeStyle(2, 0x6a7a90, 0.4);
      btn.statusText.setText(`${snap.charge}/${snap.cfg.chargeMax}`);
    }
  }

  // 兼容旧调用：旧 MainScene 仍有 hud.refreshDashCooldown(...)，把它转成 A1 通道
  // 没用上的话不会引起问题；新 MainScene 会调 refreshSkillSlots
  refreshDashCooldown(_ratio: number, _leftMs: number): void {
    // no-op —— 新版本统一走 refreshSkillSlots
  }

  // ─── Boss 全屏血条 ──────────────────────────────────────

  /**
   * 显示屏顶 Boss 血条 —— Boss 来袭时由 MainScene 调用。
   * 全宽（GAME_WIDTH-16），y = 76（在心条上方一点）；居中显示 Boss 名 + 阶段。
   */
  showBossBar(name: string): void {
    this.hideBossBar();
    const padding = 10;
    const fullW = GAME_WIDTH - padding * 2;
    const barH = 14;
    const y = 86;
    const c = this.scene.add.container(0, 0).setDepth(170);

    const bg = this.scene.add
      .rectangle(GAME_WIDTH / 2, y, fullW + 2, barH + 2, 0x000000, 0.7)
      .setStrokeStyle(1, 0xff5566, 0.85);
    const bgRect = this.scene.add
      .rectangle(padding, y - barH / 2, fullW, barH, 0x3a0a14, 0.95)
      .setOrigin(0, 0);
    const fillRect = this.scene.add
      .rectangle(padding, y - barH / 2, fullW, barH, 0xff5566, 1)
      .setOrigin(0, 0);
    const nameText = this.scene.add
      .text(GAME_WIDTH / 2, y - barH / 2 - 12, `★ ${name}`, {
        fontFamily: 'sans-serif',
        fontSize: '13px',
        fontStyle: 'bold',
        color: '#ff8a8a',
        stroke: '#1a0606',
        strokeThickness: 3,
      })
      .setOrigin(0.5, 1);
    const phaseText = this.scene.add
      .text(GAME_WIDTH - padding, y - barH / 2 - 12, '', {
        fontFamily: 'sans-serif',
        fontSize: '11px',
        fontStyle: 'bold',
        color: '#ff8a3c',
        stroke: '#1a0606',
        strokeThickness: 3,
      })
      .setOrigin(1, 1);
    c.add([bg, bgRect, fillRect, nameText, phaseText]);

    // 入场动画：从顶部滑下来
    c.y = -30;
    c.alpha = 0;
    this.scene.tweens.add({
      targets: c,
      y: 0,
      alpha: 1,
      duration: 280,
      ease: 'Cubic.easeOut',
    });

    this._bossBar = { container: c, bgRect, fillRect, nameText, phaseText, fullW };
  }

  /** 刷新当前 Boss 血量（每帧由 MainScene 在有 Boss 时调用） */
  refreshBossBar(hp: number, maxHp: number, phase: 1 | 2): void {
    if (!this._bossBar) return;
    const ratio = Math.max(0, Math.min(1, hp / Math.max(1, maxHp)));
    this._bossBar.fillRect.width = this._bossBar.fullW * ratio;
    // 二阶段变橙红
    if (phase === 2) {
      this._bossBar.fillRect.fillColor = 0xff8a3c;
      this._bossBar.phaseText.setText('狂暴 II');
    } else {
      this._bossBar.fillRect.fillColor = 0xff5566;
      this._bossBar.phaseText.setText('I');
    }
  }

  hideBossBar(): void {
    if (!this._bossBar) return;
    const c = this._bossBar.container;
    this._bossBar = null;
    this.scene.tweens.add({
      targets: c,
      y: -30,
      alpha: 0,
      duration: 240,
      onComplete: () => c.destroy(),
    });
  }

  private _buildHeartBar(maxN: number): void {
    this.hearts.forEach((h) => h.destroy());
    this.hearts = [];
    const y = 64;
    // ≥ 10 颗就切换成"♥ × N"紧凑显示，避免一排红心溢出屏宽
    this._heartCompact = maxN >= 10;
    if (this._heartCompact) {
      const t = this.scene.add
        .text(GAME_WIDTH / 2, y, '', {
          fontFamily: 'sans-serif',
          fontSize: '20px',
          fontStyle: 'bold',
          color: '#ff5577',
          stroke: '#3a0a18',
          strokeThickness: 3,
        })
        .setOrigin(0.5)
        .setDepth(100);
      this.hearts.push(t);
      return;
    }
    // 心放在顶部："得分"和"最高"下方，避开玩家手指拖动的下半屏
    const size = 18;
    const gap = 3;
    const totalW = maxN * size + (maxN - 1) * gap;
    const startX = (GAME_WIDTH - totalW) / 2 + size / 2;
    for (let i = 0; i < maxN; i++) {
      const t = this.scene.add
        .text(startX + i * (size + gap), y, '♥', {
          fontFamily: 'sans-serif',
          fontSize: `${size}px`,
          color: '#ff5577',
        })
        .setOrigin(0.5)
        .setDepth(100);
      this.hearts.push(t);
    }
  }

  /** 暴露给 Player 的输入死区（按钮命中圆）—— 由 MainScene 在 create 后调一次 */
  getInputDeadZones(): Array<{ x: number; y: number; r: number }> {
    const zones: Array<{ x: number; y: number; r: number }> = [];
    // 暂停按钮
    if (this._pauseBtnBg) {
      zones.push({ x: this._pauseBtnBg.x, y: this._pauseBtnBg.y, r: this._pauseBtnBg.radius + 4 });
    }
    // 详情按钮
    if (this._detailsBtnBg) {
      zones.push({ x: this._detailsBtnBg.x, y: this._detailsBtnBg.y, r: this._detailsBtnBg.radius + 4 });
    }
    // 3 个技能按钮（手指容易碰到，半径多给 6px）
    for (const btn of this._skillButtons) {
      zones.push({ x: btn.x, y: btn.y, r: btn.r + 6 });
    }
    return zones;
  }

  setScore(score: number): void {
    this.scoreText.setText(`得分 ${score}`);
  }

  /** 当 score 已经超过 highScore 时把顶部"最高 N"用 score 标红显示（鼓励玩家） */
  setHighScoreLive(score: number, baseHigh: number): void {
    if (score > baseHigh) {
      this.highScoreText.setText(`最高 ${score}`).setColor('#ff8a8a');
    }
  }

  refreshHearts(): void {
    const maxHp = this.player.maxHp;
    const wantCompact = maxHp >= 10;
    // maxHp 跨过阈值，或 maxHp 数量变了：重建血条
    if (wantCompact !== this._heartCompact || (!wantCompact && this.hearts.length !== maxHp)) {
      this._buildHeartBar(maxHp);
    }
    if (this._heartCompact) {
      const hp = this.player.hp;
      const t = this.hearts[0];
      t.setText(`♥ ${hp}/${maxHp}`);
      // 血量低于 30% 时整体变暗一点提示危险
      const ratio = hp / maxHp;
      if (ratio <= 0.3) t.setColor('#ff3344');
      else t.setColor('#ff5577');
      t.setAlpha(hp > 0 ? 1 : 0.4);
      return;
    }
    for (let i = 0; i < this.hearts.length; i++) {
      const alive = i < this.player.hp;
      this.hearts[i].setAlpha(alive ? 1 : 0.2);
      this.hearts[i].setText(alive ? '♥' : '♡');
    }
  }

  /**
   * @param kills            当前击杀数
   * @param chestProgress    [当前已收 / 阈值]，没传就保留旧值
   */
  refreshStats(kills: number, chestProgress?: { collected: number; threshold: number }): void {
    const p = this.player;
    const tier = this.difficulty.tier;
    this._lastKills = kills;
    if (chestProgress) this._lastChestProgress = chestProgress;

    // 顶部 4 徽章
    this._badges.wave.setText(`波 ${tier + 1}`);
    this._badges.kill.setText(`杀 ${kills}`);
    this._badges.coin.setText(`币 ${p.coins}`);
    this._badges.shield.setText(`盾 ${p.shieldCharges}`);

    // 折叠详情面板（即使隐藏也保持值同步，展开时立即正确）
    if (!this._detailsTexts) return;
    this._detailsTexts.fire.setText(`射速  ${Math.round(p.shootingDelay)}ms`);
    let dmgLine = `伤害  ${p.bulletDamage}`;
    if (p.bulletPierce > 0) dmgLine += ` 穿透+${p.bulletPierce}`;
    if (p.bulletHoming) dmgLine += ' 追';
    if (p.bulletRicochet > 0) dmgLine += ` 反${p.bulletRicochet}`;
    if (p.critChance > 0) dmgLine += ` 暴${Math.round(p.critChance * 100)}%`;
    if (p.lifestealChance > 0) dmgLine += ` 吸${Math.round(p.lifestealChance * 100)}%`;
    this._detailsTexts.dmg.setText(dmgLine);
    this._detailsTexts.shot.setText(`弹数  ${p.bulletCount}`);
    if (this._lastChestProgress) {
      const cp = this._lastChestProgress;
      this._detailsTexts.chest.setText(`宝箱  ${cp.collected}/${cp.threshold}`);
      const almost = cp.collected === cp.threshold - 1;
      this._detailsTexts.chest.setColor(almost ? '#ffe066' : '#ffb84a');
    }
  }

  /** 升档时居中闪现的"第 N 波"横幅 —— 上移到 y=0.18，避免挡住战斗区 */
  showWaveBanner(tier: number): void {
    const banner = this.scene.add
      .text(GAME_WIDTH / 2, GAME_HEIGHT * 0.18, `第 ${tier + 1} 波`, {
        fontFamily: 'sans-serif',
        fontSize: '34px',
        fontStyle: 'bold',
        color: '#ffd166',
        stroke: '#3a2a08',
        strokeThickness: 5,
      })
      .setOrigin(0.5)
      .setAlpha(0)
      .setDepth(180);
    this.scene.tweens.add({
      targets: banner,
      alpha: { from: 0, to: 1 },
      scale: { from: 0.6, to: 1 },
      duration: 200,
      onComplete: () => {
        this.scene.tweens.add({
          targets: banner,
          alpha: 0,
          duration: 600,
          delay: 600,
          onComplete: () => banner.destroy(),
        });
      },
    });
  }

  /** Boss 入场横幅：红色，比波次横幅更醒目 —— y=0.22 */
  showBossBanner(): void {
    const banner = this.scene.add
      .text(GAME_WIDTH / 2, GAME_HEIGHT * 0.22, '⚠ BOSS 来袭', {
        fontFamily: 'sans-serif',
        fontSize: '32px',
        fontStyle: 'bold',
        color: '#ff5566',
        stroke: '#1a0606',
        strokeThickness: 6,
      })
      .setOrigin(0.5)
      .setAlpha(0)
      .setDepth(181);
    this.scene.tweens.add({
      targets: banner,
      alpha: { from: 0, to: 1 },
      scale: { from: 0.6, to: 1 },
      duration: 220,
      onComplete: () => {
        this.scene.tweens.add({
          targets: banner,
          alpha: 0,
          duration: 600,
          delay: 900,
          onComplete: () => banner.destroy(),
        });
      },
    });
  }

  /** Boss 二阶段（狂暴）横幅：橙红色 + 抖动入场 —— y=0.22 */
  showEnrageBanner(): void {
    const banner = this.scene.add
      .text(GAME_WIDTH / 2, GAME_HEIGHT * 0.22, '⚠ BOSS 狂暴', {
        fontFamily: 'sans-serif',
        fontSize: '34px',
        fontStyle: 'bold',
        color: '#ff8a3c',
        stroke: '#1a0606',
        strokeThickness: 6,
      })
      .setOrigin(0.5)
      .setAlpha(0)
      .setDepth(182);
    this.scene.tweens.add({
      targets: banner,
      alpha: { from: 0, to: 1 },
      scale: { from: 1.5, to: 1 },
      duration: 200,
      ease: 'Back.easeOut',
      onComplete: () => {
        // 抖一下增强"上头"感
        this.scene.tweens.add({
          targets: banner,
          x: { from: GAME_WIDTH / 2 - 6, to: GAME_WIDTH / 2 + 6 },
          duration: 50,
          yoyo: true,
          repeat: 4,
        });
        this.scene.tweens.add({
          targets: banner,
          alpha: 0,
          duration: 500,
          delay: 800,
          onComplete: () => banner.destroy(),
        });
      },
    });
  }

  /** 天气横幅 —— y=0.28（避开屏中央战斗区） */
  showWeatherBanner(label: string, desc: string, tint: number): void {
    const tintHex = '#' + tint.toString(16).padStart(6, '0');
    const banner = this.scene.add
      .text(GAME_WIDTH / 2, GAME_HEIGHT * 0.28, `☁ ${label}`, {
        fontFamily: 'sans-serif',
        fontSize: '28px',
        fontStyle: 'bold',
        color: tintHex,
        stroke: '#0a1830',
        strokeThickness: 5,
      })
      .setOrigin(0.5)
      .setAlpha(0)
      .setDepth(180);
    const sub = this.scene.add
      .text(GAME_WIDTH / 2, GAME_HEIGHT * 0.28 + 24, desc, {
        fontFamily: 'sans-serif',
        fontSize: '13px',
        color: '#aac4d8',
      })
      .setOrigin(0.5)
      .setAlpha(0)
      .setDepth(180);
    this.scene.tweens.add({
      targets: [banner, sub],
      alpha: 1,
      duration: 200,
      onComplete: () => {
        this.scene.tweens.add({
          targets: [banner, sub],
          alpha: 0,
          duration: 500,
          delay: 1000,
          onComplete: () => { banner.destroy(); sub.destroy(); },
        });
      },
    });
  }

  /** 元素弹 buff 横幅 —— y=0.32（拾取时短暂提示，再下方） */
  showBulletBuffBanner(label: string, desc: string, tint: number): void {
    const tintHex = '#' + tint.toString(16).padStart(6, '0');
    const banner = this.scene.add
      .text(GAME_WIDTH / 2, GAME_HEIGHT * 0.32, `★ ${label}`, {
        fontFamily: 'sans-serif',
        fontSize: '26px',
        fontStyle: 'bold',
        color: tintHex,
        stroke: '#0a1830',
        strokeThickness: 5,
      })
      .setOrigin(0.5)
      .setAlpha(0)
      .setDepth(183);
    const sub = this.scene.add
      .text(GAME_WIDTH / 2, GAME_HEIGHT * 0.32 + 22, desc, {
        fontFamily: 'sans-serif',
        fontSize: '12px',
        color: '#e8f1ff',
      })
      .setOrigin(0.5)
      .setAlpha(0)
      .setDepth(183);
    this.scene.tweens.add({
      targets: [banner, sub],
      alpha: 1,
      scale: { from: 0.6, to: 1 },
      duration: 200,
      ease: 'Back.easeOut',
      onComplete: () => {
        this.scene.tweens.add({
          targets: [banner, sub],
          alpha: 0,
          duration: 400,
          delay: 1100,
          onComplete: () => { banner.destroy(); sub.destroy(); },
        });
      },
    });
  }

  // ── Combo 显示 ──────────────────────────────────────────
  /** 屏幕中央偏右的 combo 数字；实例只创建一次，靠 setText/alpha 复用 */
  private _comboText: Phaser.GameObjects.Text | null = null;
  private _comboTween: Phaser.Tweens.Tween | null = null;

  showCombo(n: number): void {
    if (n < 2) return; // 1 段不显示，避免误触发
    if (!this._comboText) {
      this._comboText = this.scene.add
        .text(GAME_WIDTH - 14, 200, '', {
          fontFamily: 'sans-serif',
          fontSize: '28px',
          fontStyle: 'bold',
          color: '#ffd166',
          stroke: '#3a2a08',
          strokeThickness: 5,
          align: 'right',
        })
        .setOrigin(1, 0.5)
        .setDepth(180);
    }
    this._comboText
      .setText(`×${n} 连击`)
      .setVisible(true)
      .setAlpha(1);
    // 大段 combo 字变红色 + 大一号
    if (n >= 10) {
      this._comboText.setColor('#ff5566').setFontSize(34);
    } else {
      this._comboText.setColor('#ffd166').setFontSize(28);
    }
    if (this._comboTween) this._comboTween.stop();
    this._comboTween = this.scene.tweens.add({
      targets: this._comboText,
      scale: { from: 1.4, to: 1 },
      duration: 180,
      ease: 'Back.easeOut',
    });
  }

  hideCombo(): void {
    if (!this._comboText) return;
    this.scene.tweens.add({
      targets: this._comboText,
      alpha: 0,
      duration: 220,
      onComplete: () => this._comboText?.setVisible(false),
    });
  }

  /** 引导 toast 队列：刚进游戏会连续触发多条提示，逐条显示避免重叠 */
  private _hintToastQueue: Array<{ text: string; durationMs: number }> = [];
  private _hintToastActive = false;

  /**
   * 拾取宝箱时的飘字 `+1 (1/3)`，金色，从指定位置飘上去淡出
   * 直接挂在场景里，不用对象池（频率低）
   */
  showChestPickupText(x: number, y: number, collected: number, threshold: number): void {
    const t = this.scene.add
      .text(x, y, `+1  宝箱 ${collected}/${threshold}`, {
        fontFamily: 'sans-serif',
        fontSize: '16px',
        fontStyle: 'bold',
        color: '#ffd166',
        stroke: '#3a2a08',
        strokeThickness: 3,
      })
      .setOrigin(0.5)
      .setDepth(150);
    this.scene.tweens.add({
      targets: t,
      y: y - 40,
      alpha: { from: 1, to: 0 },
      duration: 900,
      onComplete: () => t.destroy(),
    });
  }

  /**
   * 屏幕中下方的指引 toast（"撞 3 个宝箱可升级"），一次性提示
   * 用 alpha 渐隐，不可点击，不阻塞游戏
   */
  showHintToast(text: string, durationMs = 4500): void {
    this._hintToastQueue.push({ text, durationMs });
    if (!this._hintToastActive) this._showNextHintToast();
  }

  private _showNextHintToast(): void {
    const next = this._hintToastQueue.shift();
    if (!next) {
      this._hintToastActive = false;
      return;
    }
    this._hintToastActive = true;

    // 先创建 text 拿到真实尺寸；Phaser 的 Rectangle 几何在构造时定型，
    // 之后改 .width / .height 不会重排，必须用正确尺寸一次性建出来
    const y = 118;
    const t = this.scene.add
      .text(GAME_WIDTH / 2, y, next.text, {
        fontFamily: 'sans-serif',
        fontSize: '14px',
        color: '#ffe066',
        padding: { x: 14, y: 7 },
        align: 'center',
        wordWrap: { width: GAME_WIDTH - 48 },
      })
      .setOrigin(0.5)
      .setDepth(171);

    const bgW = t.width + 4;
    const bgH = t.height + 4;
    const bg = this.scene.add
      .rectangle(GAME_WIDTH / 2, y, bgW, bgH, 0x000000, 0.55)
      .setOrigin(0.5)
      .setDepth(170)
      .setStrokeStyle(1, 0xffd166, 0.6);

    bg.setAlpha(0);
    t.setAlpha(0);
    // 渐入
    this.scene.tweens.add({
      targets: [bg, t],
      alpha: 1,
      duration: 200,
      onComplete: () => {
        // 停留 → 渐出
        this.scene.tweens.add({
          targets: [bg, t],
          alpha: 0,
          duration: 500,
          delay: next.durationMs,
          onComplete: () => {
            bg.destroy();
            t.destroy();
            this.scene.time.delayedCall(150, () => this._showNextHintToast());
          },
        });
      },
    });
  }
}
